import { useState, type ImgHTMLAttributes } from "react";
import { IMAGE_FALLBACK, optimizedImageUrl, buildSrcSet, defaultSizes } from "@/lib/image-url";


/**
 * SafeImage — unified image loading strategy for cards/boxes across the site.
 *
 * - decoding="async" (off-main-thread decode)
 * - loading="lazy" by default; pass `eager` for above-the-fold LCP images
 * - fetchPriority: "high" when eager, otherwise "low"
 * - referrerPolicy="no-referrer" (better CDN cache hits, avoids blocked hotlinks)
 * - Built-in skeleton shimmer + blurred navy/gold gradient background,
 *   removed only after `onLoad` — no gray/white flash while loading, and
 *   the same soft gradient stays visible if the image fails.
 * - Graceful onError: hides the broken image so the container's skeleton
 *   stays visible instead of showing a broken-image icon.
 *
 * NOTE: Intentionally NOT used by InteractiveWorldMap, which keeps its own
 * dedicated loading pipeline.
 */
type Props = ImgHTMLAttributes<HTMLImageElement> & { eager?: boolean; noSkeleton?: boolean };

export function SafeImage({
  eager,
  noSkeleton,
  onError,
  onLoad,
  loading,
  decoding,
  fetchPriority,
  referrerPolicy,
  className = "",
  src,
  srcSet,
  sizes,
  ...rest
}: Props) {
  const [state, setState] = useState<"loading" | "loaded" | "failed">(
    "loading"
  );
  // Remote images are re-encoded to WebP and resized by a caching image CDN.
  // Step 0 = optimized WebP, 1 = original URL, 2 = local placeholder.
  const [step, setStep] = useState(0);

  const intrinsicWidth =
    typeof rest.width === "number" ? rest.width : Number(rest.width) || undefined;

  const proxied = optimizedImageUrl(src, intrinsicWidth);
  const chain = [proxied, src, IMAGE_FALLBACK].filter(Boolean) as string[];
  const currentSrc = chain[Math.min(step, chain.length - 1)];

  // Responsive sizing only applies while we're on the optimized CDN step.
  const responsiveSrcSet = step === 0 ? srcSet ?? buildSrcSet(src, intrinsicWidth) : undefined;
  const responsiveSizes = responsiveSrcSet ? sizes ?? defaultSizes(intrinsicWidth) : sizes;

  // Skeleton shimmer stays behind the image until it paints.
  // Once loaded, the class is swapped out so decoded pixels aren't
  // composited over an animated background (saves paint cost).
  // `noSkeleton` disables the effect for images that should stay plain
  // (e.g., logos with transparent backgrounds).
  const skeletonClass = noSkeleton
    ? ""
    : state === "loaded"
      ? "img-skeleton-loaded"
      : "img-skeleton";

  if (state === "failed") {
    // Preserve the caller-provided sizing (h-full w-full, w-6 h-6, etc.) so
    // the fallback occupies the exact same box as the image would have. The
    // inline `display:block` guard prevents a `<span>` from collapsing to
    // inline-flow height when the parent relies on the child to fill it.
    return (
      <span
        className={`${className} ${noSkeleton ? "" : "img-skeleton"}`}
        style={{ display: "block" }}
        aria-hidden
      />
    );
  }

  return (
    <img
      {...rest}
      src={currentSrc}
      srcSet={responsiveSrcSet}
      sizes={responsiveSizes}
      className={`${className} ${skeletonClass}`.trim()}
      loading={eager ? "eager" : loading ?? "lazy"}
      decoding={decoding ?? "async"}
      fetchPriority={eager ? "high" : fetchPriority ?? "low"}
      referrerPolicy={referrerPolicy ?? "no-referrer"}
      onLoad={(e) => {
        setState("loaded");
        onLoad?.(e);
      }}
      onError={(e) => {
        // Retry down the chain (proxy → original → local placeholder) before
        // giving up, so a 429 never surfaces as a broken image in an audit.
        if (step < chain.length - 1) {
          setStep((s) => s + 1);
          return;
        }
        setState("failed");
        onError?.(e);
      }}
    />
  );
}

export default SafeImage;
